import { supabase } from './supabase';
import { storyFields } from '@/lib/field-sets';

/**
 * Story interface
 */
export interface Story {
  id: string;
  name: string;
  plotline: string | null;
  status: 'draft' | 'in_progress' | 'completed';
  genre: string | null;
  created_at: string;
  updated_at: string;
  project_id: string;
}

/**
 * Extended story interface with related data
 */
export interface StoryWithRelations extends Story {
  characters?: any[];
  chapters?: any[];
  timeline_events?: any[];
  story_entities?: any[];
}

/**
 * Get all stories for a project
 * @param projectId - Project ID
 * @returns Promise with array of stories
 */
export const getStoriesByProject = async (projectId: string): Promise<Story[]> => {
  const { data, error } = await supabase
    .from('stories')
    .select(storyFields.list)
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching stories:', error);
    throw error;
  }

  return (data || []) as unknown as Story[];
};

/**
 * Get a story by ID
 * @param storyId - Story ID
 * @param includeFull - Whether to include full related data
 * @returns Promise with story
 */
export const getStoryById = async <T extends boolean = false>(
  storyId: string,
  includeFull: T = false as T
): Promise<T extends true ? StoryWithRelations : Story> => {
  const { data, error } = await supabase
    .from('stories')
    .select(includeFull ? storyFields.full : storyFields.detail)
    .eq('id', storyId)
    .single();

  if (error) {
    console.error('Error fetching story:', error);
    throw error;
  }

  return data as unknown as T extends true ? StoryWithRelations : Story;
};

/**
 * Create a new story
 * @param storyData - Story data
 * @returns Promise with created story
 */
export const createStory = async (storyData: {
  project_id: string;
  name: string;
  plotline?: string;
  genre?: string;
  status?: 'draft' | 'in_progress' | 'completed';
}): Promise<Story> => {
  // Normalize the data for DB
  const normalizedData = {
    project_id: storyData.project_id,
    name: storyData.name,
    plotline: storyData.plotline || '',
    genre: storyData.genre || null,
    status: storyData.status || 'draft',
  };

  const { data, error } = await supabase
    .from('stories')
    .insert(normalizedData)
    .select()
    .single();

  if (error) {
    console.error('Error creating story:', error);
    throw error;
  }

  return data;
};

/**
 * Update a story
 * @param id - Story ID
 * @param updates - Story updates
 * @returns Promise with updated story
 */
export const updateStory = async (
  id: string,
  updates: Partial<Omit<Story, 'id'>>
): Promise<Story> => {
  const { data, error } = await supabase
    .from('stories')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating story:', error);
    throw error;
  }

  return data;
};

/**
 * Delete a story
 * @param id - Story ID
 * @returns Promise with success status
 */
export const deleteStory = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('stories')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting story:', error);
    throw error;
  }
};

/**
 * Set a story as the current story for a project
 * @param projectId - Project ID
 * @param storyId - Story ID
 * @returns Promise with updated project
 */
export const setCurrentStory = async (
  projectId: string,
  storyId: string
): Promise<any> => {
  const { data, error } = await supabase
    .from('projects')
    .update({ current_story_id: storyId })
    .eq('id', projectId)
    .select()
    .single();

  if (error) {
    console.error('Error setting current story:', error);
    throw error;
  }

  return data;
};

/**
 * Get the current story for a project
 * @param projectId - Project ID
 * @returns Promise with current story
 * @deprecated Use getCurrentStoryOptimized instead
 */
export const getCurrentStory = async (projectId: string): Promise<Story | null> => {
  // First get the project to find the current_story_id
  const { data: project, error: projectError } = await supabase
    .from('projects')
    .select('current_story_id')
    .eq('id', projectId)
    .single();

  if (projectError) {
    console.error('Error fetching project:', projectError);
    throw projectError;
  }

  if (!project?.current_story_id) {
    return null;
  }

  // Then get the story
  return getStoryById(project.current_story_id);
};

/**
 * Get the current story for a project in a single optimized query
 * @param projectId - Project ID
 * @param fields - Fields to select from the story
 * @returns Promise with current story
 */
export const getCurrentStoryOptimized = async (
  projectId: string,
  fields: string = storyFields.minimal
): Promise<Story | null> => {
  try {
    // First get the project with current_story_id
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('current_story_id')
      .eq('id', projectId)
      .single();

    if (projectError) {
      console.error('Error fetching project:', projectError);
      throw projectError;
    }

    if (!project?.current_story_id) {
      return null;
    }

    // Then get the story with the specified fields
    const { data: story, error: storyError } = await supabase
      .from('stories')
      .select(fields)
      .eq('id', project.current_story_id)
      .single();

    if (storyError) {
      console.error('Error fetching story:', storyError);
      throw storyError;
    }

    return story as unknown as Story;
  } catch (error) {
    console.error('Error in getCurrentStoryOptimized:', error);
    throw error;
  }
};
